// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import THEntityLayout from '@Organisms/THEntityLayout/THEntityLayout';
import AlertLayoutSider from '@Organisms/Alert/AlertLayoutSider/AlertLayoutSider';
import { HeaderActionType, RouteType } from '@Types';
import { ResponderDrawer } from '@Organisms/Responder';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import AlertMergeDrawer from '@Organisms/Alert/AlertMerge/AlertMergeDrawer';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import ChildRoutes from '@Pages/Alerts/Details/routes';
import { CaseOutput, AlertOutput, CustomStatusMapType, AlertStatusStage, PermissionsEnum } from 'thehive-sdk';
import useOneQuery from '@Hooks/useOneQuery';
import client from '@Client';
import { computeEventList } from '@Organisms/Alert/eventList';
import { ICONS } from '@Constants/iconsApp';
import { AlertChangeStatus } from '@Organisms/Alert/AlertChangeStatus/AlertChangeStatus';
import { StatusContext } from '@Context/StatusProvider';
import { computeGetSelector } from '@Organisms/Alert/selector';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import { useGetAccessibleRoutes } from '@Helpers/getAccessibleRoutes';
import { CaseCreateManage } from '@Organisms/Case/CaseCreate/CaseCreateManage';
import { ScopeContext } from '@Context/ScopeProvider';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { useCurrentUser } from '@Queries/userQueries';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';

import './Details.scss';

const cycleStage = [AlertStatusStage.NEW, AlertStatusStage.IN_PROGRESS, AlertStatusStage.CLOSED];

const AlertLayout = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();
    const { hasCortex } = useContext(StatusContext);
    const { data: currentUser } = useCurrentUser();

    const { permissions = [] } = currentUser || {};
    const [alertStatus, setAlertStatus] = useState<CustomStatusMapType>({});
    const hasPermissions = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_ALERT_UPDATE,
        PermissionsEnum.MANAGE_ALERT_DELETE,
        PermissionsEnum.MANAGE_ALERT_IMPORT,
    ]);

    const accessibleRoutes = useGetAccessibleRoutes(ChildRoutes);

    const { alertId } = useParams<{ alertId: string }>();

    const { data: alertData, refetch: dataRefetch } = useOneQuery<AlertOutput>({
        selector: computeGetSelector({ alertId }),
        controller: client.AlertController,
    });

    const {
        customStatus: {
            alert: { list: alertStatusList },
        },
    } = useContext(ScopeContext);

    const {
        title = '',
        follow,
        caseId = '',
        summary = '',
        stage,
        tlp,
        pap,
        severity,
        date,
        customFields,
        tags,
        description,
        caseTemplate = '',
    } = alertData || {};

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.AlertController,
                locale: i,
                onSuccess: (count, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    dataRefetch();
                },
                onError: handleNotificationError,
                history,
            }),
        [i, dataRefetch, history],
    );

    const HeaderActions: HeaderActionType[] = [
        {
            label: i('alert.actions.create-case-from-alert.label'),
            icon: <ICONS.CreateIcon />,
            hidden: !!caseId,
            disabled: !hasPermissions.importAlert,
            Action: (triggerElem): ReactElement => (
                <CaseCreateManage
                    initialValues={{
                        tlp,
                        pap,
                        severity,
                        startDate: date,
                        customFields,
                        tags,
                        description,
                        title,
                    }}
                    defaultCaseTemplate={caseTemplate}
                    title={i('alert.actions.create-case-several.label')}
                    triggerElem={triggerElem}
                    onImport={(formData) => {
                        return client.CaseController.import(formData)
                            .then(({ case: { _id: caseId } }) => client.AlertController.import(alertId, caseId))
                            .then(({ _id: caseId }) => {
                                openMessage('success', i('notification.alert.create-case-several'));
                                history.push(RouteBuilder.compute('cases.details', { caseId }));
                            })
                            .catch(handleNotificationError);
                    }}
                    onMispImport={(formData) => {
                        return client.MispController.import(formData)
                            .then(({ _id: caseId }) => client.AlertController.import(alertId, caseId))
                            .then(({ _id: caseId }) => {
                                openMessage('success', i('notification.alert.create-case-several'));
                                history.push(RouteBuilder.compute('cases.details', { caseId }));
                            })
                            .catch(handleNotificationError);
                    }}
                    onCaseCreate={(caseData) => {
                        return client.AlertController.createCase(alertId, caseData)
                            .then((data) => {
                                openMessage('success', i('notification.alert.create-case-several'));
                                if (data) {
                                    const { _id: caseId } = data;
                                    history.push(RouteBuilder.compute('cases.details', { caseId }));
                                }
                                return data;
                            })
                            .catch(handleNotificationError);
                    }}
                />
            ),
        },
        {
            label: i('alert.actions.merge-alerts.label'),
            icon: <ICONS.MergeIcon />,
            hidden: !!caseId,
            disabled: !hasPermissions.importAlert,
            Action: (triggerElem): ReactElement => (
                <AlertMergeDrawer
                    triggerElem={triggerElem}
                    listCasesForMerge={(filter) =>
                        client.CaseController.list<CaseOutput[]>({
                            selector: {
                                head: [{ name: 'listCase' }],
                                name: 'get-cases-for-merge',
                                extraData: ['status'],
                            },
                            filters: [filter],
                            paginationOptions: { page: 0, itemsPerPage: 20 },
                        })
                    }
                    onMergeAlertsIntoCase={(caseId) => eventList.onMergeSeveralIntoCase([alertId], caseId)}
                />
            ),
        },
        {
            label: i('alert.actions.unlink.label'),
            icon: <ICONS.LinkOutlined />,
            hidden: !caseId,
            disabled: !hasPermissions.updateAlert,
            Action: (triggerElem) => (
                <THPopConfirm
                    title={i('alert.actions.unlink.confirm')}
                    onConfirm={() => eventList.onUnlinkFromCase(alertId, caseId)}
                >
                    {triggerElem}
                </THPopConfirm>
            ),
        },
        {
            label: i('alert.actions.follow.label'),
            hidden: follow,
            icon: <ICONS.EyeIcon />,
            disabled: !hasPermissions.updateAlert,
            Action: (triggerElem): ReactElement =>
                React.cloneElement(triggerElem, {
                    onClick: () => eventList.onFollow(alertId),
                }),
        },
        {
            label: i('alert.actions.unfollow.label'),
            hidden: !follow,
            icon: <ICONS.EyeInvisibleIcon />,
            disabled: !hasPermissions.updateAlert,
            Action: (triggerElem): ReactElement =>
                React.cloneElement(triggerElem, {
                    onClick: () => eventList.onUnfollow(alertId),
                }),
        },
        {
            disabled: !hasPermissions.updateAlert || !alertStatus.InProgress || stage === AlertStatusStage.IMPORTED,
            Action: (triggerElem): ReactElement => (
                <AlertChangeStatus
                    triggerElem={triggerElem}
                    onEdit={(alertData) => eventList.onEdit(alertData, alertId, stage === AlertStatusStage.NEW)}
                    statusList={{ [AlertStatusStage.IN_PROGRESS]: alertStatus.InProgress }}
                    initialValues={{
                        status: alertStatus?.[AlertStatusStage.IN_PROGRESS]?.[0].value || '',
                        summary,
                        customFields,
                    }}
                />
            ),
            ...(stage !== AlertStatusStage.CLOSED
                ? { label: i('alert.actions.start.label'), icon: <ICONS.StartIcon /> }
                : { label: i('alert.actions.restart.label'), icon: <ICONS.ReloadOutlined /> }),
        },
        {
            label: i('alert.actions.close.label'),
            icon: <ICONS.CloseIcon />,
            disabled: !hasPermissions.updateAlert || !alertStatus.Closed || stage === AlertStatusStage.IMPORTED,
            Action: (triggerElem): ReactElement => (
                <AlertChangeStatus
                    triggerElem={triggerElem}
                    onEdit={(alertData) => eventList.onEdit(alertData, alertId, stage === AlertStatusStage.NEW)}
                    statusList={{ [AlertStatusStage.CLOSED]: alertStatus.Closed || [] }}
                    initialValues={{
                        status: alertStatus?.[AlertStatusStage.CLOSED]?.[0].value || '',
                        summary,
                        customFields,
                    }}
                    useCustomField
                />
            ),
        },
        {
            label: i('alert.actions.delete.label'),
            icon: <ICONS.DeleteIcon />,
            className: 'actionlist-icon__warning',
            disabled: !hasPermissions.deleteAlert,
            Action: (triggerElem): ReactElement => (
                <THPopConfirm title={i('alert.actions.delete.single')} onConfirm={() => eventList.onDelete(alertId)}>
                    {triggerElem}
                </THPopConfirm>
            ),
        },
        {
            label: i('alert.actions.responders.label'),
            icon: <ICONS.RunResponderIcon />,
            disabled: !hasPermissions.updateAlert,
            Action: (triggerElem): ReactElement => (
                <ResponderDrawer triggerElem={triggerElem} objectId={alertId} objectType="alert" />
            ),
            hidden: !hasCortex,
        },
    ];

    useEffect(() => {
        if (alertStatusList.length) {
            const stageIndex = cycleStage.indexOf((stage || '') as AlertStatusStage);
            const alertStatusMap = alertStatusList.reduce((acc, curr) => {
                const stagePosition = cycleStage.indexOf((curr.stage || '') as AlertStatusStage);
                if (
                    curr.stage === AlertStatusStage.IMPORTED ||
                    (stageIndex >= stagePosition && stage !== AlertStatusStage.CLOSED) ||
                    (curr.stage !== AlertStatusStage.IN_PROGRESS && stage === AlertStatusStage.CLOSED)
                ) {
                    return acc;
                }

                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setAlertStatus(alertStatusMap);
        }
    }, [alertStatusList, stage]);

    useStreamObserver(
        { rootId: alertId, objectType: STREAM_EVENTS.ALERT },
        (data) => {
            const canRefetch = data.some(
                (action) => action.base.action === 'update' && action.base._createdBy !== currentUser?.login,
            );

            if (canRefetch) {
                dataRefetch();
            }
        },
        [currentUser?.login],
    );

    return (
        <THEntityLayout
            className="layout-alert"
            routes={accessibleRoutes}
            leftSiderContent={
                !!alertData && (
                    <AlertLayoutSider
                        data={alertData}
                        eventList={eventList}
                        hasPermissions={hasPermissions.updateAlert}
                    />
                )
            }
            title={<CopyValue value={title}>{title}</CopyValue>}
            actions={HeaderActions}
            entityId={alertId}
            paramName="alertId"
        />
    );
};

const BreadcrumbLabel = (): ReactElement => {
    const match = useRouteMatch('/alerts/:alertId');

    const { alertId = '' } = (match?.params as any) || {};

    const { data: alertData } = useOneQuery<AlertOutput>({
        selector: computeGetSelector({ alertId }),
        controller: client.AlertController,
        manual: true,
        fetchOnStart: false,
    });

    return (
        <>{`${alertData?.type} (#${
            alertData?.sourceRef?.substring(0, 10) + ((alertData?.sourceRef?.length || 0) > 10 ? '...' : '')
        })`}</>
    );
};

export const Route: RouteType = {
    path: RouteBuilder.compute('alerts.layout'),
    content: <AlertLayout />,
    space: ['user'],
    Context: ListProvider,
    breadcrumb: {
        label: <BreadcrumbLabel />,
        isLink: false,
    },
};
